import { html, TemplateResult } from 'lit';
import { LitComponent } from './LitComponent.ts';

type LoadingProgressView = {
  loaded: number;
  total: number;
  percent: number | null;
  detail: string;
};

export class LoadingComponent extends LitComponent {
  private message: string = 'Loading...';
  private size: 'sm' | 'md' | 'lg' = 'md';
  private progress: LoadingProgressView | null = null;
  /** Sets props. */


  setProps(props: {
    message?: string;
    size?: 'sm' | 'md' | 'lg';
    progress?: LoadingProgressView | { loaded: number; total: number } | null;
  }): void {
    if (props.message !== undefined) this.message = props.message;
    if (props.size !== undefined) this.size = props.size;
    if (props.progress !== undefined) {
      this.progress =
        props.progress == null
          ? null
          : 'detail' in props.progress
            ? props.progress
            : {
                loaded: props.progress.loaded,
                total: props.progress.total,
                percent:
                  props.progress.total > 0
                    ? Math.min(100, Math.round((props.progress.loaded / props.progress.total) * 100))
                    : null,
                detail:
                  props.progress.total > 0
                    ? `${Math.min(100, Math.round((props.progress.loaded / props.progress.total) * 100))}%`
                    : `${props.progress.loaded} B`,
              };
    }
    this.update();
  }
  /** Returns template. */


  protected getTemplate(): TemplateResult {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-8 w-8',
      lg: 'h-12 w-12',
    };
    const progress = this.progress;
    const indeterminate = progress != null && progress.percent == null;

    return html`
      <div class="flex flex-col items-center justify-center p-4" role="status" aria-live="polite" aria-busy="true">
        <svg
          class="loading-spinner animate-spin ${sizeClasses[this.size]}"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="loading-spinner__track"
            cx="12"
            cy="12"
            r="10"
            stroke="var(--md-sys-color-outline-variant)"
            stroke-width="4"
          ></circle>
          <path
            class="loading-spinner__arc"
            fill="var(--md-sys-color-primary)"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        ${this.message
          ? html`<p class="loading-spinner__label mt-3 text-[0.875rem] leading-snug">${this.message}</p>`
          : null}
        ${progress && progress.loaded > 0
          ? html`
              <div
                class="loading-progress mt-4 w-full min-w-[12rem] max-w-[18rem]"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow=${progress.percent ?? undefined}
                aria-valuetext=${progress.detail}
              >
                <div class="loading-progress__track">
                  <div
                    class="loading-progress__fill ${indeterminate ? 'loading-progress__fill--indeterminate' : ''}"
                    style=${indeterminate
                      ? ''
                      : `width: ${progress.percent ?? 0}%`}
                  ></div>
                </div>
                <p class="loading-progress__label mt-1.5 text-center text-xs tabular-nums">${progress.detail}</p>
              </div>
            `
          : null}
      </div>
      <style>
        .loading-spinner__label {
          color: var(--md-sys-color-on-surface-variant);
          font-weight: 400;
          letter-spacing: 0.015em;
          text-align: center;
        }
        .loading-progress__track {
          height: 0.5rem;
          width: 100%;
          overflow: hidden;
          border-radius: 9999px;
          background: var(--md-sys-color-surface-container-highest);
        }
        .loading-progress__fill {
          height: 100%;
          border-radius: inherit;
          background: var(--md-sys-color-primary);
          transition: width 300ms ease;
        }
        .loading-progress__fill--indeterminate {
          width: 100%;
          opacity: 0.45;
          animation: loading-progress-pulse 1.2s ease-in-out infinite;
        }
        .loading-progress__label {
          color: var(--md-sys-color-on-surface-variant);
        }
        @keyframes loading-progress-pulse {
          0%,
          100% {
            opacity: 0.35;
          }
          50% {
            opacity: 0.85;
          }
        }
      </style>
    `;
  }
}

customElements.define('loading-component', LoadingComponent);
